import React, { useMemo, useState } from 'react';
import { CARDS, CARD_POOL_DIVINE, CARD_POOL_DIVINE_CLASS } from '../game/cards';
import { AvatarChip } from './Avatar';
import CodexCard from './CodexCard';
import { Copy, Check, Sparkles } from 'lucide-react';

// v3.1 DIVINE COLLECTION — shared component used in:
//   - Codex "DIVINE" tab (all classes visible)
//   - Profile page (ownership badges)
// Props:
//   pickCounts: { [divine_id]: play_count } — from profile.divine_pick_counts
//   classFilter?: 'rizzler'|'sigma'|'gooner'|null — if provided, only that class's locked divine is shown
//   showShare?: bool — render copy-share badge (defaults true)
//   playerTag?: string — used for the share text
export default function DivineCollection({ pickCounts = {}, classFilter = null, showShare = true, playerTag = null, avatar = null, frame = null }) {
  const [copied, setCopied] = useState(false);

  const allDivines = useMemo(() => {
    // Base pool + one class-locked divine per class
    const items = CARD_POOL_DIVINE.map(id => ({ id, classLocked: null }));
    for (const [cls, arr] of Object.entries(CARD_POOL_DIVINE_CLASS)) {
      for (const id of arr) items.push({ id, classLocked: cls });
    }
    // Optional filter — hide class-locked divines that aren't yours
    if (classFilter) {
      return items.filter(it => !it.classLocked || it.classLocked === classFilter);
    }
    return items;
  }, [classFilter]);

  const owned = allDivines.filter(it => (pickCounts[it.id] || 0) > 0).length;
  const total = allDivines.length;
  const pct = total > 0 ? Math.round((owned / total) * 100) : 0;
  const totalPlays = Object.values(pickCounts || {}).reduce((a, n) => a + n, 0);

  const shareText =
    `${playerTag ? `@${playerTag} ` : ''}✦ DIVINE COLLECTION: ${owned}/${total} (${pct}%)\n` +
    `${totalPlays} divine plays across all runs\n` +
    `Featured: ${allDivines.filter(it => (pickCounts[it.id] || 0) > 0).slice(0, 3).map(it => CARDS[it.id]?.name || it.id).join(', ') || 'none yet'}\n` +
    `Play @ backendbrainrot.com`;

  const copyShare = () => {
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="w-full" data-testid="divine-collection">
      {/* Completion badge */}
      <div className="flex items-center gap-4 flex-wrap mb-4">
        <div
          className="brutal-border-dim px-4 py-3 bg-[#1a1400] flex items-center gap-3"
          style={{ borderColor: '#fff2a8', boxShadow: '4px 4px 0 #fff2a8' }}
          data-testid="divine-completion-badge"
        >
          {avatar && <AvatarChip avatar={avatar} frame={frame} size={40} testid="divine-collection-avatar"/>}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#fff2a8] font-mono flex items-center gap-1">
              <Sparkles size={12}/> DIVINE COLLECTION
            </div>
            <div className="font-display text-3xl font-black text-[#fff2a8]" data-testid="divine-completion-fraction">
              {owned}<span className="text-white/60 text-2xl">/{total}</span>
              <span className="ml-3 text-lg text-white" data-testid="divine-completion-pct">{pct}%</span>
            </div>
            <div className="text-[9px] uppercase font-mono tracking-widest text-white/50 mt-1">
              {totalPlays} total plays{playerTag ? ` · ${playerTag}` : ''}
            </div>
          </div>
        </div>

        {showShare && (
          <button
            className="brutal-btn text-xs px-3 py-2"
            onClick={copyShare}
            data-testid="divine-share-button"
          >
            {copied ? <Check size={14}/> : <Copy size={14}/>} {copied ? 'COPIED' : 'COPY FLEX'}
          </button>
        )}
      </div>

      {/* Slot grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 justify-items-center" data-testid="divine-collection-grid">
        {allDivines.map(({ id, classLocked }) => {
          const c = CARDS[id];
          const count = pickCounts[id] || 0;
          const isOwned = count > 0;
          const cls = classLocked ? classLocked.toUpperCase() : null;
          const clsColor = cls === 'RIZZLER' ? '#ff007f' : cls === 'SIGMA' ? '#39ff14' : cls === 'GOONER' ? '#00e5ff' : null;
          const footer = (
            <div className="flex items-center justify-between gap-2">
              {cls && (
                <span style={{ color: clsColor }} data-testid={`divine-slot-class-${id}`}>{cls}</span>
              )}
              <span className="text-[color:var(--pink)]" data-testid={`divine-slot-count-${id}`}>×{count}</span>
            </div>
          );
          return (
            <div key={id} data-testid={`divine-slot-${id}`} data-owned={isOwned ? 'true' : 'false'}>
              <CodexCard
                testid={`divine-card-${id}`}
                art={c?.art}
                name={c?.name || 'DIVINE'}
                type={cls ? `${cls} DIVINE` : 'DIVINE'}
                cost={c?.cost}
                costLabel="COST"
                costColor="#fff2a8"
                desc={c?.desc || ''}
                footer={footer}
                accent="#fff2a8"
                locked={!isOwned}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
